// Studio — Streamer Go-Live dashboard
import { useEffect, useState, useRef } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import DashboardQuickActions from "../components/DashboardQuickActions";
import { renderSafeMarkdown } from "../lib/safeMarkdown";
import CohostInbox from "../components/CohostInbox";
import LiveKitSetupCard, { LiveKitConnectModal } from "../components/LiveKitSetupCard";
import LiveKitStreamRoom from "../components/LiveKitStreamRoom";
import ContactNexus from "../components/ContactNexus";
import ParentalStudioTab from "../components/ParentalStudioTab";
import ComingSoon from "../components/ComingSoon";
import { Link, useNavigate } from "react-router-dom";
import { Radio, Power, Copy, Eye, DollarSign, Sparkles, Settings, Camera, Mic, Cast, AlertCircle, Bot, Film, Scissors, ShoppingBag, TrendingUp, Users, MessageSquare, Send, UserPlus, Download } from "lucide-react";
import { toast, Toaster } from "sonner";

// Game categories that show up as buckets on the Landing highlights row + /highlights/v2.
// Adding new ones is safe — /highlights/v2 accepts arbitrary ?games= values.
const OBS_GAMES = [
  "Just Chatting",
  "Call of Duty: Warzone",
  "Apex Legends",
  "Fortnite",
  "Valorant",
  "GTA V",
  "Minecraft",
  "League of Legends",
  "IRL",
  "Music",
  "Podcast",
  "Mobile Live",
  "Live OBS",  // fallback / catch-all — lands in the All Games rail
];

export default function Studio() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [rev, setRev] = useState(null);
  const [form, setForm] = useState({ title: "", thumbnail: "", is_r18: false, game_category: "" });
  const [obsGame, setObsGame] = useState("Live OBS");
  const [obsTitle, setObsTitle] = useState("");
  const [obsKeyBusy, setObsKeyBusy] = useState(false);
  const obsKeySubmittingRef = useRef(false);
  const [categories, setCategories] = useState([]);
  const [lkRoom, setLkRoom] = useState(null);
  const [showLkModal, setShowLkModal] = useState(false);
  const [activeTab, setActiveTab] = useState("live");   // "live" | "content" | "insights"
  const lkCardRef = useRef(null);
  useEffect(() => { api.get("/game-categories").then(({ data }) => setCategories(data)).catch(() => {}); }, []);
  useEffect(() => { if (user) api.get("/me/livekit-room").then(({ data }) => setLkRoom(data)).catch(() => {}); }, [user]);

  const load = () => {
    api.get("/studio/overview").then(({ data }) => setData(data));
    api.get("/creator/revenue").then(({ data }) => setRev(data)).catch(() => {});
  };
  useEffect(() => {
    if (user) load();
  }, [user]);

  if (!user) return <div className="p-20 text-center text-white/40">Sign in as creator or owner.</div>;
  if (user.role !== "creator" && user.role !== "owner") {
    return (
      <div className="p-20 text-center space-y-4">
        <Radio className="w-12 h-12 mx-auto text-nexus-cyan" />
        <h2 className="font-display text-2xl">Upgrade to Studio</h2>
        <p className="text-white/50">Streamer Studio is part of the Studio tier.</p>
        <Link to="/pricing" className="btn-nexus inline-flex">See tiers</Link>
      </div>
    );
  }
  if (!data) return <div className="text-white/40">Loading studio…</div>;

  const goLive = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    if (!form.game_category) { toast.error("Pick a game category"); return; }
    // Per-user LiveKit gate — no navigation. Open the embedded card in-place.
    try {
      const { data: lk } = await api.get("/me/livekit/projects");
      if (!lk?.projects?.length) { setShowLkModal(true); return; }
    } catch { /* soft-fail — backend will still reject */ }
    try {
      await api.post("/streams/safety-check", { title: form.title, category: form.game_category, is_r18: form.is_r18 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Safety check failed");
      return;
    }
    try {
      const { data: s } = await api.post("/studio/go_live", { ...form, game_category: form.game_category });
      toast.success("You are LIVE.");
      load();
      setTimeout(() => nav(`/watch/${s.id}`), 700);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to go live";
      toast.error(msg, { duration: 8000 });
      if (/livekit|live streaming|connect your/i.test(msg)) setShowLkModal(true);
    }
  };
  const endStream = async (sid) => {
    await api.post(`/studio/end/${sid}`);
    toast.success("Stream ended");
    load();
  };
  const endAllStreams = async () => {
    if (!data?.live_streams?.length) return;
    await api.post("/studio/end_all");
    toast.success(`Ended ${data.live_streams.length} live stream${data.live_streams.length === 1 ? "" : "s"}`);
    load();
  };

  return (
    <div className="max-w-[1500px] mx-auto space-y-10" data-testid="studio-page">
      <Toaster theme="dark" position="top-center" />
      <LiveKitConnectModal open={showLkModal} onClose={() => setShowLkModal(false)} onSetupClick={() => lkCardRef.current?.openAndScroll()} />
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2 text-nexus-magenta">// studio · go live</div>
          <h1 className="font-display font-black text-4xl lg:text-6xl tracking-tighter">{user.display_name}, the deck is yours.</h1>
          <DashboardQuickActions className="mt-3" />
        </div>
        <div className="flex items-center gap-3">
          {data.live_streams.length > 0 && (
            <button
              onClick={endAllStreams}
              data-testid="end-all-streams"
              className="bg-nexus-crimson text-white px-5 py-3 font-display tracking-[0.3em] uppercase text-xs hover:bg-nexus-crimson/80 transition-all animate-pulse"
            >
              ● END STREAM ({data.live_streams.length})
            </button>
          )}
          <div className={`px-3 py-1.5 text-[10px] tracking-[0.3em] font-display uppercase ${(data.livekit_configured || lkRoom?.activated) ? "bg-nexus-mint/15 text-nexus-mint border border-nexus-mint/40" : "bg-nexus-amber/15 text-nexus-amber border border-nexus-amber/40"}`}>
            {(data.livekit_configured || lkRoom?.activated) ? "● LiveKit Ready" : "○ LiveKit Not Configured"}
          </div>
          <div className="px-3 py-1.5 text-[10px] tracking-[0.3em] font-display uppercase bg-white/5 border border-white/10 text-white/70">{(data.membership_tier || "free").toUpperCase()} TIER</div>
          <ContactNexus variant="inline" />
        </div>
      </div>

      {/* Floating mobile End Stream button (always visible on mobile when live) */}
      {data.live_streams.length > 0 && (
        <button
          onClick={endAllStreams}
          data-testid="end-stream-floating"
          className="lg:hidden fixed bottom-24 left-1/2 -translate-x-1/2 z-40 bg-nexus-crimson text-white px-6 py-3 font-display tracking-[0.3em] uppercase text-xs shadow-[0_0_24px_rgba(255,51,102,0.5)] animate-pulse"
        >
          ● END STREAM
        </button>
      )}

      {/* TAB BAR — Live (default) · Content · Insights · Parental */}
      <div className="flex gap-1 border-b border-white/10 overflow-x-auto" role="tablist" data-testid="studio-tabs">
        {[
          { key: "live", label: "Live" },
          { key: "content", label: "Content" },
          { key: "insights", label: "Insights" },
          { key: "parental", label: "Parental" },
        ].map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={activeTab === t.key}
            onClick={() => setActiveTab(t.key)}
            data-testid={`studio-tab-${t.key}`}
            className={`px-5 py-2.5 text-xs tracking-[0.3em] uppercase font-display transition-colors -mb-px ${
              activeTab === t.key
                ? "text-nexus-cyan border-b-2 border-nexus-cyan"
                : "text-black/70 hover:text-black border-b-2 border-transparent"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "live" && (
      <div className="space-y-10" data-testid="studio-tab-panel-live">

      {/* PERSONAL LIVEKIT ROOM + MIC/CAM PRE-FLIGHT */}
      <div className="grid lg:grid-cols-2 gap-4" data-testid="livekit-preflight">
        <div className="glass p-5 space-y-3 border-l-2 border-nexus-mint">
          <div className="flex items-center justify-between">
            <div className="label-eyebrow text-nexus-mint flex items-center gap-2"><Radio className="w-3.5 h-3.5" />// your personal livekit room</div>
            {lkRoom?.activated ? (
              <span className="text-[9px] tracking-[0.3em] uppercase text-nexus-mint">● Active</span>
            ) : (
              <span className="text-[9px] tracking-[0.3em] uppercase text-nexus-amber">○ Pending</span>
            )}
          </div>
          <div className="font-mono text-[11px] text-white/80 break-all bg-nexus-surface/60 px-3 py-2 border border-white/5" data-testid="lk-room-id">
            {lkRoom?.room || "Loading…"}
          </div>
          {lkRoom?.url && (
            <div className="text-[10px] text-white/40">Edge: <span className="font-mono text-white/60">{lkRoom.url}</span></div>
          )}
          <p className="text-[11px] text-white/50 leading-relaxed">Auto-provisioned the moment you signed up. Use it for instant 1-tap broadcasts, co-streams, or 1:1 video calls — no setup required.</p>
        </div>
      </div>

      {/* GO LIVE PANEL */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 relative aspect-video bg-nexus-surface border border-white/10 overflow-hidden flex items-center justify-center">
          {(() => {
            // If the creator has a live stream running with a LiveKit room, embed
            // their own subscriber view so they can see themselves, chat & stickers
            // in real-time and end the stream in-place. Otherwise fall back to the
            // static onboarding placeholder.
            const activeStream = data.live_streams.find((s) => s.livekit_room) || data.live_streams[0];
            if (activeStream && activeStream.livekit_room) {
              return (
                <div className="absolute inset-0" data-testid="camera-preview-live">
                  <LiveKitStreamRoom
                    streamId={activeStream.id}
                    role="self_view"
                    fallback={<div className="w-full h-full bg-nexus-surface flex items-center justify-center text-white/40 text-xs">Connecting to your LiveKit room…</div>}
                  />
                </div>
              );
            }
            return (
              <>
                <img src="https://static.prod-images.emergentagent.com/jobs/2e932df3-26ef-43be-9078-73e8341543f5/images/9c0b2dcae3ddd3a0fa6ab5d11dbdfaf1c47ba488d68fda3389ee47f02fbb4c2a.png" alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
                <div className="relative text-center space-y-3 z-10" data-testid="camera-preview-placeholder">
                  <Camera className="w-12 h-12 mx-auto text-nexus-cyan/80" />
                  <div className="font-display tracking-[0.3em] text-sm uppercase">Camera Preview</div>
                  <div className="text-xs text-white/40">Connect via OBS/StreamLabs with your stream key →</div>
                </div>
                <div className="absolute bottom-4 left-4 flex items-center gap-2">
                  <button className="btn-ghost !py-2 !px-3 text-[9px]"><Mic className="w-3 h-3" /> Mic</button>
                  <button className="btn-ghost !py-2 !px-3 text-[9px]"><Camera className="w-3 h-3" /> Cam</button>
                  <button className="btn-ghost !py-2 !px-3 text-[9px]"><Cast className="w-3 h-3" /> Screen</button>
                </div>
              </>
            );
          })()}
        </div>

        <div className="glass p-6 space-y-4" data-testid="go-live-panel">
          <div className="label-eyebrow">// go live</div>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Stream title" className="w-full bg-transparent border border-white/10 px-3 py-3 text-sm" data-testid="go-live-title" />
          <input value={form.thumbnail} onChange={(e) => setForm({ ...form, thumbnail: e.target.value })} placeholder="Thumbnail URL (optional)" className="w-full bg-transparent border border-white/10 px-3 py-3 text-sm" />
          <div className="space-y-2" data-testid="game-category-picker">
            <div className="label-eyebrow text-nexus-cyan">// game category · required</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {categories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setForm({ ...form, game_category: c.id, game_title: c.examples[0] })}
                  className={`text-[10px] tracking-[0.12em] uppercase py-2 px-2 border font-display rounded-full transition-colors whitespace-nowrap overflow-hidden text-ellipsis ${
                    form.game_category === c.id ? "border-nexus-cyan text-nexus-cyan bg-nexus-cyan/10" : "border-white/10 text-white/50 hover:text-white"
                  }`}
                  data-testid={`cat-${c.id}`}
                  title={c.examples.join(", ")}
                >
                  {c.label}
                </button>
              ))}
            </div>
            {form.game_category && (
              <input
                value={form.game_title || ""}
                onChange={(e) => setForm({ ...form, game_title: e.target.value })}
                placeholder={`Game title (e.g. ${(categories.find((c) => c.id === form.game_category)?.examples || []).join(", ")})`}
                className="w-full bg-transparent border border-white/10 px-3 py-2 text-xs"
                data-testid="game-title-input"
              />
            )}
            {/* Hot-game quick-picks — these populate the Spotlight rails on /explore */}
            <div className="space-y-1 pt-1">
              <div className="text-[9px] tracking-[0.3em] text-nexus-amber uppercase font-display">// hot games — quick fill</div>
              <div className="flex flex-wrap gap-1">
                {["Warzone", "Apex Legends", "Fortnite", "Valorant", "League of Legends", "CS2", "Minecraft", "GTA V", "Rocket League"].map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setForm({ ...form, game_category: form.game_category || "fps", game_title: g })}
                    data-testid={`quick-game-${g.replace(/\s+/g, '-')}`}
                    className={`text-[9px] tracking-[0.18em] uppercase px-2 py-1 border font-display transition-colors ${
                      form.game_title === g ? "border-nexus-amber text-nexus-amber bg-nexus-amber/10" : "border-white/10 text-white/40 hover:text-white"
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <label className="flex items-start gap-2 cursor-pointer p-2 border border-white/10 hover:border-nexus-amber/60 transition-colors" data-testid="go-live-r18-wrap">
            <input
              type="checkbox"
              checked={form.is_r18}
              onChange={(e) => setForm({ ...form, is_r18: e.target.checked })}
              className="mt-0.5 accent-nexus-amber"
              data-testid="go-live-r18"
            />
            <div className="flex-1">
              <div className="text-[11px] tracking-[0.2em] uppercase text-nexus-amber font-display">R18+ content</div>
              <div className="text-[10px] text-white/50 mt-0.5 leading-snug">Strong language, gore, mature themes. Auto-hidden from child accounts. <span className="text-nexus-crimson">Nudity / sexual content is banned platform-wide.</span></div>
            </div>
          </label>
          <button onClick={goLive} className="btn-nexus w-full justify-center" data-testid="go-live-btn"><Radio className="w-3.5 h-3.5" /> Start Streaming</button>
          <LiveKitSetupCard ref={lkCardRef} compact subtitle="Required to go live. Add up to 10 projects for auto-fallback on quota." />
          {!data.livekit_configured && (
            <div className="text-[11px] text-nexus-amber flex items-start gap-2 leading-relaxed">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              Stream will use the demo placeholder until LiveKit keys are added in Owner HQ → Integrations.
            </div>
          )}
        </div>
      </div>

      </div>
      )}

      {activeTab === "insights" && (
      <div className="space-y-10" data-testid="studio-tab-panel-insights">

      {/* REAL-TIME EARNINGS DASHBOARD */}
      {rev && (
        <section className="space-y-4" data-testid="creator-earnings">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="font-display text-sm tracking-[0.3em] uppercase flex items-center gap-2"><DollarSign className="w-4 h-4 text-nexus-cyan" />Live earnings · NX & AUD</h2>
            <div className="flex items-center gap-3">
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/40">rate: 1 NX = ${rev.payout_rate_aud_per_nx}</div>
              <Link to="/verify" data-testid="verify-link" className={`text-[10px] tracking-[0.3em] uppercase px-3 py-1.5 border ${rev.kyc_verified ? "border-nexus-mint text-nexus-mint" : "border-nexus-amber text-nexus-amber"}`}>
                {rev.kyc_verified ? "● Verified" : "○ Verify to withdraw"}
              </Link>
            </div>
          </div>
          <div className="grid lg:grid-cols-4 gap-px bg-white/5">
            <div className="bg-nexus-panel border-l-2 border-nexus-cyan p-6">
              <div className="label-eyebrow text-nexus-cyan">// net lifetime</div>
              <div className="font-display font-black text-4xl mt-3 text-nexus-cyan text-glow-cyan">{rev.total_net_lifetime.toLocaleString()} <span className="text-base text-white/40">NX</span></div>
              <div className="text-sm text-white/60 mt-1 font-mono">≈ ${rev.lifetime_aud_equivalent.toLocaleString()} AUD</div>
            </div>
            <div className="bg-nexus-surface p-6">
              <div className="label-eyebrow">// today</div>
              <div className="font-display font-black text-3xl mt-3">{rev.today_net.toFixed(0)} <span className="text-sm text-white/40">NX</span></div>
              <div className="text-xs text-white/50 mt-1 font-mono">${rev.today_aud_equivalent.toFixed(2)} AUD</div>
            </div>
            <div className="bg-nexus-surface p-6">
              <div className="label-eyebrow">// 7-day</div>
              <div className="font-display font-black text-3xl mt-3">{rev.week_net.toFixed(0)} <span className="text-sm text-white/40">NX</span></div>
              <div className="text-xs text-white/50 mt-1 font-mono">${rev.week_aud_equivalent.toFixed(2)} AUD</div>
            </div>
            <div className="bg-nexus-surface p-6">
              <div className="label-eyebrow">// wallet</div>
              <div className="font-display font-black text-3xl mt-3 text-nexus-amber">{rev.wallet_balance.toFixed(0)} NX</div>
              <div className="text-xs text-white/50 mt-1 font-mono">${rev.wallet_balance_aud.toFixed(2)} AUD</div>
            </div>
          </div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/40">{rev.show_usd_publicly ? "✓ AUD value visible on your public profile" : "○ AUD value hidden from public profile"}</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5">
            {[
              { k: "tips",          l: "Tips & Donations",  share: rev.shares.tips, c: "#FF007F" },
              { k: "paid_stickers", l: "Paid Stickers",     share: rev.shares.tips, c: "#FFC107" },
              { k: "subscriptions", l: "Subscriptions",     share: rev.shares.subs, c: "#00F0FF" },
              { k: "ad_revenue",    l: "Ad Revenue",        share: rev.shares.ads,  c: "#8A2BE2" },
            ].map((src) => {
              const s = rev.by_source[src.k];
              return (
                <div key={src.k} className="bg-nexus-surface p-5" data-testid={`earn-${src.k}`}>
                  <div className="flex items-center justify-between">
                    <div className="label-eyebrow">{src.l}</div>
                    <div className="text-[9px] tracking-[0.3em] uppercase font-display" style={{ color: src.c }}>{src.share}</div>
                  </div>
                  <div className="font-display font-black text-2xl mt-3" style={{ color: src.c }}>${s.net.toFixed(2)}</div>
                  <div className="text-[11px] text-white/40 tracking-wider mt-1">gross ${s.gross.toFixed(2)}</div>
                  <div className="hairline my-3" />
                  <div className="grid grid-cols-2 gap-2 text-[10px] tracking-wider">
                    <div><span className="text-white/40">today </span><span className="font-mono">${s.today.toFixed(2)}</span></div>
                    <div><span className="text-white/40">7d </span><span className="font-mono">${s.week.toFixed(2)}</span></div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="grid sm:grid-cols-3 gap-px bg-white/5">
            <div className="bg-nexus-surface p-4 text-center"><div className="label-eyebrow">subscribers</div><div className="font-display font-black text-2xl mt-2">{rev.active_subscribers}</div></div>
            <div className="bg-nexus-surface p-4 text-center"><div className="label-eyebrow">live streams</div><div className="font-display font-black text-2xl mt-2">{rev.live_streams}</div></div>
            <div className="bg-nexus-surface p-4 text-center"><div className="label-eyebrow">total viewers</div><div className="font-display font-black text-2xl mt-2">{rev.total_viewers.toLocaleString()}</div></div>
          </div>
        </section>
      )}
      {/* REAL-TIME EARNINGS DASHBOARD — moved to Insights tab. See below. */}

      </div>
      )}

      {activeTab === "parental" && (
        <div data-testid="studio-tab-panel-parental">
          <ParentalStudioTab />
        </div>
      )}

      {activeTab === "live" && (
      <div className="space-y-10">

      <div className="glass p-5 space-y-4" data-testid="obs-ingress-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="label-eyebrow text-nexus-mint flex items-center gap-2"><Radio className="w-3 h-3" />// OBS · Streamlabs · OBS Mobile · Larix</div>
            <h4 className="font-display font-bold text-base mt-1">RTMP ingest credentials</h4>
            <p className="text-[11px] text-white/55 mt-1 leading-relaxed">
              Paste these into OBS → Settings → Stream → Service: <span className="text-white">Custom</span>. Anything you broadcast appears in your NEXUS stream automatically.
            </p>
          </div>
          {!data.ingress_ready ? (
            <button onClick={async () => {
              // Synchronous ref guard -- state-based disabled={obsKeyBusy} alone isn't
              // enough against a rapid double-click/double-tap since React state
              // updates are deferred, and a race here used to create two duplicate
              // stream docs for the same room (reported live by a beta tester).
              if (obsKeySubmittingRef.current) return;
              obsKeySubmittingRef.current = true; setObsKeyBusy(true);
              try {
                await api.post("/livekit/ingress/create", { name: "OBS / Streamlabs", game: obsGame, title: obsTitle });
                toast.success("OBS credentials generated"); load();
              } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
              finally { obsKeySubmittingRef.current = false; setObsKeyBusy(false); }
            }} disabled={obsKeyBusy} className="btn-nexus !border-nexus-mint disabled:opacity-50" data-testid="generate-obs-key">{obsKeyBusy ? "Generating…" : "Generate OBS key"}</button>
          ) : (
            <button onClick={async () => {
              if (obsKeySubmittingRef.current) return;
              if (!window.confirm("Rotate the stream key? Your existing OBS profile will stop working until you paste the new one.")) return;
              obsKeySubmittingRef.current = true; setObsKeyBusy(true);
              try {
                await api.post("/livekit/ingress/create", { name: "OBS / Streamlabs", rotate: true, game: obsGame, title: obsTitle });
                toast.success("New key generated"); load();
              } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
              finally { obsKeySubmittingRef.current = false; setObsKeyBusy(false); }
            }} disabled={obsKeyBusy} className="btn-ghost !border-nexus-amber !text-nexus-amber disabled:opacity-50" data-testid="rotate-obs-key">{obsKeyBusy ? "Rotating…" : "Rotate key"}</button>
          )}
        </div>

        {/* Game category picker — lands OBS streams in the right highlight tile (Warzone/Apex/Fortnite/etc). */}
        <div className="border border-white/10 bg-nexus-surface/40 p-3 rounded-lg space-y-3" data-testid="obs-category-picker">
          <div className="label-eyebrow flex items-center gap-2 text-nexus-cyan"><Sparkles className="w-3 h-3" />// stream category</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <div className="label-eyebrow mb-1 text-white/50">// game</div>
              <select
                value={obsGame}
                onChange={(e) => setObsGame(e.target.value)}
                className="w-full bg-nexus-surface border border-white/15 px-3 py-2 text-sm rounded-md"
                data-testid="obs-game-select"
              >
                {OBS_GAMES.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <div className="label-eyebrow mb-1 text-white/50">// title</div>
              <input
                value={obsTitle}
                onChange={(e) => setObsTitle(e.target.value.slice(0, 120))}
                placeholder="e.g. Warzone Solo Grind"
                className="w-full bg-nexus-surface border border-white/15 px-3 py-2 text-sm rounded-md"
                data-testid="obs-title-input"
              />
            </div>
          </div>
          {data.ingress_ready && (
            <button
              onClick={async () => {
                try {
                  await api.patch("/livekit/ingress/stream-info", { game: obsGame, title: obsTitle || null });
                  toast.success("Stream info updated");
                  load();
                } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
              }}
              className="btn-ghost !border-nexus-cyan !text-nexus-cyan text-[10px]"
              data-testid="obs-apply-stream-info"
            >
              Apply to current stream
            </button>
          )}
          <div className="text-[10px] text-white/40 leading-relaxed">
            ⓘ Pick <b>Call of Duty: Warzone</b> and your OBS stream lands in the Warzone highlight tile on the Landing page. Change any time — no need to rotate your key.
          </div>
        </div>

        {/* HOW TO TUTORIAL */}
        <details className="border border-white/10 bg-nexus-surface/40" data-testid="obs-tutorial" open>
          <summary className="cursor-pointer px-3 py-2 text-[11px] tracking-[0.2em] uppercase font-display text-nexus-cyan hover:text-white transition-colors">
            ▾ How to set up OBS in 60 seconds (READ THIS if you got &quot;no config url&quot;)
          </summary>

          {/* HARD-RED WARNING for the OBS Enhanced Broadcasting bug */}
          <div className="mx-4 my-3 border border-nexus-crimson/50 bg-nexus-crimson/10 p-3 rounded-lg" data-testid="obs-enhanced-broadcasting-warning">
            <div className="text-[11px] font-display font-bold tracking-widest text-nexus-crimson uppercase mb-1">⚠︎ Fix &quot;no config url for current service&quot;</div>
            <div className="text-[12px] text-white/85 leading-relaxed">
              This is a <span className="text-nexus-crimson">known OBS bug</span> (v30.2.2+): the &quot;<b>Enable Enhanced Broadcasting</b>&quot; option (Twitch feature) breaks Custom-RTMP streaming. Do this <b>before</b> pasting our URL:
              <ol className="list-decimal list-inside mt-2 space-y-0.5 text-white/80">
                <li>OBS → <span className="font-mono">Settings → Stream</span></li>
                <li>Set Service to <b>Twitch</b> (temporarily)</li>
                <li><b>UNCHECK</b> <span className="font-mono">Enable Enhanced Broadcasting</span></li>
                <li>Click <b>Apply</b>, then close &amp; reopen OBS</li>
                <li>Come back to Settings → Stream, set Service to <b>Custom…</b></li>
                <li>Paste our Server URL + Stream Key below</li>
              </ol>
            </div>
          </div>

          <ol className="px-4 py-3 space-y-2 text-[12px] text-white/70 leading-relaxed list-decimal list-inside">
            <li><span className="text-white">Download OBS</span> (free) from <span className="font-mono text-nexus-cyan">obsproject.com</span>. Mobile: <span className="text-white">Larix Broadcaster</span> (iOS/Android) or <span className="text-white">Streamlabs Mobile</span>.</li>
            <li>Click <span className="text-nexus-mint font-bold">Generate OBS key</span>. Copy the <span className="text-white">Server URL</span> and <span className="text-white">Stream Key</span>.</li>
            <li>In OBS: <span className="font-mono text-white">Settings → Stream → Service: <b>Custom…</b></span> (NOT Twitch, NOT YouTube). Paste URL into <span className="text-white">Server</span>, key into <span className="text-white">Stream Key</span>.</li>
            <li>Add your sources — Display Capture / Window Capture / Video Capture Device.</li>
            <li>Hit <span className="text-nexus-mint font-bold">Start Streaming</span> in OBS. Stream lands in your NEXUS LiveKit room at <span className="font-mono text-nexus-cyan">/watch/&lt;stream-id&gt;</span>.</li>
            <li>Tip: our URL uses <span className="font-mono text-nexus-cyan">rtmps://</span> (encrypted). If your ISP or corporate network blocks port 443/rtmps, you&apos;ll see &quot;connection failed&quot; — try a different network or your mobile hotspot.</li>
          </ol>
          <div className="px-4 pb-3 text-[10px] text-white/40 italic">
            ⓘ The key acts like a password. Never share it. Click <span className="text-nexus-amber">Rotate key</span> if it leaks.
          </div>
        </details>

        {data.ingress_ready ? (
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <div className="label-eyebrow mb-1">// server url</div>
              <div className="flex items-center gap-2 bg-nexus-surface border border-white/10 p-3 font-mono text-[11px]">
                <span className="flex-1 truncate">{data.ingest_url}</span>
                <button onClick={() => { navigator.clipboard?.writeText(data.ingest_url); toast.success("Server URL copied"); }} className="text-white/40 hover:text-nexus-cyan" data-testid="copy-ingest-url"><Copy className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <div>
              <div className="label-eyebrow mb-1">// stream key (keep secret)</div>
              <div className="flex items-center gap-2 bg-nexus-surface border border-white/10 p-3 font-mono text-[11px]">
                <span className="flex-1 truncate">{data.stream_key}</span>
                <button onClick={() => { navigator.clipboard?.writeText(data.stream_key); toast.success("Stream key copied"); }} className="text-white/40 hover:text-nexus-cyan" data-testid="copy-stream-key"><Copy className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            {/* One-click OBS profile importer — bypasses the Enhanced Broadcasting bug entirely
                because OBS reads the JSON directly instead of resolving a preset service. */}
            <div className="sm:col-span-2 flex flex-wrap gap-2 pt-1">
              <button
                data-testid="download-obs-profile"
                onClick={() => {
                  // OBS service.json spec — see obsproject/obs-studio /UI/data/services.json
                  const profile = {
                    "settings": {
                      "server": data.ingest_url,
                      "key": data.stream_key,
                      "use_auth": false,
                      "bwtest": false,
                    },
                    "type": "rtmp_custom",
                  };
                  const blob = new Blob([JSON.stringify(profile, null, 2)], { type: "application/json" });
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(blob);
                  a.download = "nexus-obs-service.json";
                  a.click();
                  URL.revokeObjectURL(a.href);
                  toast.success("Downloaded — Copy to OBS profile folder");
                }}
                className="btn-ghost !border-nexus-mint !text-nexus-mint text-[10px] tracking-[0.25em] uppercase font-display inline-flex items-center gap-1.5"
              >
                <Download className="w-3.5 h-3.5" /> Download OBS profile (auto-fill)
              </button>
              <a
                href="https://obsproject.com/help#help-menu-import"
                target="_blank"
                rel="noreferrer"
                data-testid="obs-import-help"
                className="text-[10px] text-white/40 hover:text-nexus-cyan self-center underline"
              >How to import (obsproject.com)</a>
            </div>
          </div>
        ) : (
          <div className="text-[11px] text-white/40 italic">No OBS key generated yet. Click <span className="text-nexus-mint">Generate OBS key</span> above — takes a second. Your camera + mic from OBS will then flow into the same LiveKit room as the browser streamer.</div>
        )}
      </div>

      {/* LIVE STREAMS list (Recent tips moved to Insights tab) */}
      <section className="space-y-3" data-testid="live-streams">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-sm tracking-[0.3em] uppercase">Your live streams</h2>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/40">{data.live_streams.length} active</div>
        </div>
        <div className="space-y-px bg-white/5">
          {data.live_streams.length === 0 && <div className="bg-nexus-surface p-6 text-white/40 text-sm">No streams live right now. Go live above.</div>}
          {data.live_streams.map((s) => (
            <div key={s.id} className="bg-nexus-surface p-4 space-y-3">
              <div className="flex items-center gap-4">
                <img src={s.thumbnail} alt="" className="w-20 aspect-video object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{s.title}</div>
                  <div className="text-[11px] text-white/40 mt-1 flex items-center gap-3"><Eye className="w-3 h-3" />{s.viewers.toLocaleString()} <DollarSign className="w-3 h-3 ml-2" />{s.total_tips.toFixed(2)}</div>
                </div>
                <Link to={`/watch/${s.id}`} className="btn-ghost !py-2 !px-3 text-[9px]">View</Link>
                <button onClick={() => endStream(s.id)} className="btn-ghost !py-2 !px-3 text-[9px] !border-nexus-crimson !text-nexus-crimson" data-testid={`end-${s.id}`}><Power className="w-3 h-3" /> End</button>
              </div>
              <CohostInbox streamId={s.id} />
            </div>
          ))}
        </div>
      </section>

      </div>
      )}

      {activeTab === "insights" && (
      <div className="space-y-10">

      <section className="space-y-3" data-testid="recent-tips-section">
        <h2 className="font-display text-sm tracking-[0.3em] uppercase flex items-center gap-2"><Sparkles className="w-4 h-4 text-nexus-cyan" />Recent tips & stickers</h2>
        <div className="space-y-px bg-white/5">
          {data.recent_tips.length === 0 && <div className="bg-nexus-surface p-6 text-white/40 text-sm">No tips yet. They'll appear here in real-time.</div>}
          {data.recent_tips.map((t) => (
            <div key={t.id} className="bg-nexus-surface p-4 flex items-center gap-3">
              {t.sticker && <div className="text-2xl" style={{ filter: `drop-shadow(0 0 8px ${t.sticker.color})` }}>{t.sticker.emoji}</div>}
              <div className="flex-1 min-w-0">
                <div className="text-sm"><span className="text-nexus-cyan font-semibold">{t.user_name}</span> tipped</div>
                <div className="text-[10px] text-white/40">{new Date(t.ts).toLocaleString()}</div>
              </div>
              <div className="font-mono text-nexus-amber">+{t.amount.toFixed(0)} NX</div>
            </div>
          ))}
        </div>
      </section>

      <EngagementStats />

      </div>
      )}

      {activeTab === "content" && (
      <div className="space-y-10" data-testid="studio-tab-panel-content">

      <MarketingTools />
      <LibraryAndClips />

      <section className="grid md:grid-cols-1 max-w-sm gap-px bg-white/5">
        {[
          { l: "Custom Stickers", d: "Studio tier — design your own animated stickers and set NX prices. Channel-exclusive.", link: "/studio/tools" },
        ].map((c) => (
          <Link to={c.link} key={c.l} className="bg-nexus-surface p-6 group hover:bg-nexus-panel transition-colors block" data-testid={`tool-${c.l}`}>
            <Settings className="w-4 h-4 text-nexus-cyan mb-3" />
            <div className="font-display font-bold">{c.l}</div>
            <div className="text-xs text-white/50 mt-2 leading-relaxed">{c.d}</div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-nexus-cyan mt-3">Open Tools →</div>
          </Link>
        ))}
      </section>

      <Ed3nCoach />

      </div>
      )}

    </div>
  );
}

/* ============================== ED3N AI COACH (inline) ============================== */
function Ed3nCoach() {
  const [prompt, setPrompt] = useState("");
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const ask = async () => {
    if (!prompt.trim()) return;
    setBusy(true); setReply("");
    try {
      const { data } = await api.post("/eden/ask", { prompt, context: "creator" });
      setReply(data.reply);
    } catch (e) { toast.error(e?.response?.data?.detail || "ED3N error"); }
    setBusy(false);
  };
  return (
    <section className="glass p-6 space-y-3 border-l-2 border-nexus-violet" data-testid="ed3n-coach">
      <div className="flex items-center gap-2">
        <Bot className="w-5 h-5 text-nexus-violet" />
        <div className="label-eyebrow text-nexus-violet">// ed3n · your stream coach</div>
      </div>
      <p className="text-xs text-white/60">Ask anything — title ideas, audience growth, schedule tips, retention tactics.</p>
      <div className="flex gap-2">
        <input value={prompt} onChange={(e) => setPrompt(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} placeholder="e.g. 'Suggest a viral stream title for my Warzone run tonight'" className="flex-1 bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="ed3n-prompt" />
        <button onClick={ask} disabled={busy} className="btn-nexus" data-testid="ed3n-ask"><Send className="w-3 h-3" />{busy ? "…" : "Ask"}</button>
      </div>
      {reply && <div className="ed3n-output bg-nexus-surface p-4 border border-white/10 text-sm text-white/80" data-testid="ed3n-reply" dangerouslySetInnerHTML={{ __html: renderSafeMarkdown(reply) }} />}
    </section>
  );
}

/* ============================== ENGAGEMENT STATS ============================== */
function EngagementStats() {
  const [e, setE] = useState(null);
  useEffect(() => { api.get("/studio/engagement").then(({ data }) => setE(data)).catch(() => {}); }, []);
  if (!e) return null;
  const cards = [
    { l: "Followers", v: e.followers, c: "#FF007F", i: Users },
    { l: "Active subs", v: e.active_subscribers, c: "#00F0FF", i: UserPlus },
    { l: "Chat msgs", v: e.chat_messages_lifetime.toLocaleString(), c: "#FFC107", i: MessageSquare },
    { l: "Tips received", v: e.tips_count_lifetime, c: "#8A2BE2", i: Sparkles },
    { l: "Tips NX total", v: `${e.tips_nx_total.toFixed(0)} NX`, c: "#00FF66", i: DollarSign },
    { l: "Clips", v: e.clips_count, c: "#00F0FF", i: Film },
    { l: "Clip downloads", v: e.clip_downloads, c: "#FF6EC7", i: Download },
    { l: "Total viewers", v: e.total_viewers_lifetime.toLocaleString(), c: "#FFD700", i: Eye },
  ];
  return (
    <section className="space-y-3" data-testid="engagement-stats">
      <h2 className="font-display text-sm tracking-[0.3em] uppercase flex items-center gap-2"><TrendingUp className="w-4 h-4 text-nexus-cyan" />Engagement · lifetime</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/5">
        {cards.map((c) => (
          <div key={c.l} className="bg-nexus-surface p-4">
            <c.i className="w-3.5 h-3.5 mb-2" style={{ color: c.c }} />
            <div className="label-eyebrow">{c.l}</div>
            <div className="font-display font-black text-2xl mt-1" style={{ color: c.c }}>{c.v}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ============================== MARKETING TOOLS + MERCH SLOT + BREELS ============================== */
function MarketingTools() {
  const [t, setT] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/studio/marketing_tools").then(({ data }) => setT(data)).catch(() => {}); }, []);
  if (!t) return null;
  return (
    <section className="space-y-4" data-testid="marketing-tools">
      <h2 className="font-display text-sm tracking-[0.3em] uppercase flex items-center gap-2"><Sparkles className="w-4 h-4 text-nexus-amber" />Marketing tools & free samples</h2>
      <div className="grid lg:grid-cols-3 gap-px bg-white/5">
        <div className="bg-nexus-surface p-5 space-y-3">
          <div className="label-eyebrow text-nexus-mint">// free samples</div>
          <ul className="text-xs text-white/70 space-y-1.5">
            {t.free_samples.map((s) => (
              <li key={s.id} className="flex justify-between"><span>· {s.name}</span><Link to={s.url} className="text-nexus-cyan text-[10px] tracking-wider">OPEN →</Link></li>
            ))}
          </ul>
        </div>
        <div className="bg-nexus-surface p-5 space-y-3">
          <div className="label-eyebrow text-nexus-cyan">// share templates</div>
          {t.marketing_templates.map((m) => (
            <div key={m.id} className="space-y-1">
              <div className="text-[11px] text-white/70 font-medium">{m.name} <span className="text-white/40 text-[9px]">· {m.channel}</span></div>
              <div className="text-[10px] text-white/50 bg-black/40 p-2 font-mono whitespace-pre-wrap">{m.template}</div>
              <button onClick={() => { navigator.clipboard?.writeText(m.template); toast.success("Template copied"); }} className="text-[9px] tracking-[0.3em] uppercase text-nexus-cyan hover:underline">Copy</button>
            </div>
          ))}
        </div>
        <div className="bg-nexus-surface p-5 space-y-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2"><Film className="w-4 h-4 text-nexus-violet" /><div className="label-eyebrow text-nexus-violet">// breels ai (studio)</div></div>
            <ComingSoon
              title="BReels is loading"
              subtitle="AI short-film tools are being rebuilt as their own flagship project. Stream & clip tools stay fully live."
              accent="violet"
              icon={Film}
              testId="studio-breels-coming-soon"
              compact
            />
          </div>
          <div className="space-y-2 border-t border-white/10 pt-3">
            <div className="flex items-center gap-2"><ShoppingBag className="w-4 h-4 text-nexus-amber" /><div className="label-eyebrow text-nexus-amber">// elysium wear slot</div></div>
            <ComingSoon
              title="Elysium Wear incoming"
              subtitle="Creator-designed drops with stream-overlay checkout arrive with the next NEXUS wave."
              accent="amber"
              icon={ShoppingBag}
              testId="studio-elysium-wear-coming-soon"
              compact
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================== LIBRARY + CLIPS (auto-save & download) ============================== */
function LibraryAndClips() {
  const [lib, setLib] = useState(null);
  const load = () => api.get("/library/me").then(({ data }) => setLib(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!lib) return null;

  const saveReplay = async (streamId) => {
    try { await api.post(`/studio/streams/${streamId}/save_replay`); toast.success("Replay saved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const trackDownload = async (cid) => { await api.post(`/clips/${cid}/download`); load(); };
  const deleteClip = async (cid) => { await api.delete(`/clips/${cid}`); toast.success("Deleted"); load(); };

  return (
    <section className="space-y-4" data-testid="studio-library">
      <h2 className="font-display text-sm tracking-[0.3em] uppercase flex items-center gap-2"><Scissors className="w-4 h-4 text-nexus-cyan" />Library · Clips · Replays · BReels</h2>
      <div className="text-[11px] text-white/40 leading-relaxed">
        Auto-save your live streams as replays. Viewers create clips on the Watch page — they all land here. Click to download or share.
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div>
          <div className="label-eyebrow mb-2">// replays ({lib.replays.length})</div>
          <div className="space-y-px bg-white/5">
            {lib.replays.length === 0 && <div className="bg-nexus-surface p-4 text-white/40 text-xs">No replays yet. Save a stream replay from Watch.</div>}
            {lib.replays.map((r) => (
              <div key={r.id} className="bg-nexus-surface p-3 flex items-center gap-3">
                <img src={r.thumbnail} alt="" className="w-16 aspect-video object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{r.title}</div>
                  <div className="text-[10px] text-white/40">{new Date(r.saved_at).toLocaleDateString()}</div>
                </div>
                {r.source_url && <a href={r.source_url} download className="text-nexus-cyan"><Download className="w-3.5 h-3.5" /></a>}
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="label-eyebrow mb-2">// clips ({lib.clips.length})</div>
          <div className="space-y-px bg-white/5">
            {lib.clips.length === 0 && <div className="bg-nexus-surface p-4 text-white/40 text-xs">No clips yet.</div>}
            {lib.clips.map((c) => (
              <div key={c.id} className="bg-nexus-surface p-3 flex items-center gap-3" data-testid={`clip-${c.id}`}>
                <img src={c.thumbnail} alt="" className="w-16 aspect-video object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{c.title}</div>
                  <div className="text-[10px] text-white/40">{c.duration.toFixed(0)}s · {c.downloads} downloads</div>
                </div>
                {c.source_url && <a href={c.source_url} download onClick={() => trackDownload(c.id)} className="text-nexus-cyan"><Download className="w-3.5 h-3.5" /></a>}
                <button onClick={() => deleteClip(c.id)} className="text-nexus-crimson text-[10px]">×</button>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="label-eyebrow mb-2">// breels</div>
          <ComingSoon
            title="BReels is loading"
            subtitle="Shelved while BReels v2 is being built as its own flagship project."
            accent="violet"
            icon={Film}
            testId="studio-library-breels-coming-soon"
            compact
          />
        </div>
      </div>
    </section>
  );
}

/* ============================== CO-STREAM INVITE (retired) ==============================
   The push-invite flow was replaced by TikTok-style request-to-join.
   Viewers hit the "Request to Co-Stream" button on /watch/:id → the streamer accepts
   from CohostInbox (visible above per live stream). No component needed here.
   ======================================================================================= */


